import { useEffect, useState } from 'react';
import { getAuthUrl, type AuthService } from 'src/contstant/apiEndpoints';

type Service = AuthService;

/**
 * Required backing services that mint their own session cookie after Midway auth. Each is
 * a separate Coral service behind nginx Sentry; the cookie is set by the service the first
 * time it sees the Midway-authenticated request, so we walk through `/ping` for every
 * service before any business call works. A failure to warm up a required service is fatal
 * — the app cannot function without it, so app readiness (`isReady`) gates on these.
 */
const REQUIRED_SERVICES: readonly Service[] = ['orbit', 'helios'];

/**
 * Best-effort services warmed up in the background. Starlight/Astra backs the supplementary
 * copilot feature; a Starlight outage — or its absence in a stage where it is not yet
 * deployed — must never gate `isReady`. Its warm-up is fire-and-forget with errors
 * swallowed; the copilot surfaces its own error when opened.
 */
const BEST_EFFORT_SERVICES: readonly Service[] = ['starlight'];

/** How long we'll wait for a single iframe load before giving up. */
const IFRAME_TIMEOUT_MS = 8_000;

/**
 * Grace period after `iframe.onload` before we tear the iframe down.
 * Without this, mobile Safari frequently fires `onload` before the
 * Set-Cookie response body has been parsed and committed to the cookie
 * jar — yanking the iframe at that moment loses the cookie. Mirrors
 * the same delay used in the legacy desktop AuthHandler.
 */
const COOKIE_COMMIT_DELAY_MS = 1_000;

/**
 * Timeout for the probe fetch. The probe is just an optimization for
 * return visits — if it hangs, we want to fall through to the iframe
 * path quickly rather than blocking the app in a loading state.
 */
const PROBE_TIMEOUT_MS = 3_000;

/**
 * Result of the warm-up.
 * - `isLoading`: at least one service hasn't established a session yet.
 * - `isReady`: every service has its cookie in place; safe to issue API calls.
 * - `isError` / `error`: a service failed to mint its cookie within the
 *    timeout. The provider should surface this as an auth error.
 */
export interface UseServiceSessionsResult {
  readonly isLoading: boolean;
  readonly isReady: boolean;
  readonly isError: boolean;
  readonly error: Error | null;
}

/**
 * Probe a service's `/ping` URL with a credentialed top-level fetch.
 * If the cookie is already in place, the response is 200 OK and we
 * can skip iframe warm-up entirely. This is the fast path for return
 * visits and for users navigating between pages.
 *
 * Mirrors `checkServiceAccess` in `src/components/auth/AuthHandler.tsx`.
 */
const checkServiceAccess = async (service: Service): Promise<boolean> => {
  try {
    const response = await fetch(getAuthUrl(service), {
      credentials: 'include',
      mode: 'cors',
      cache: 'no-cache',
      signal: AbortSignal.timeout(PROBE_TIMEOUT_MS),
    });
    return response.ok;
  } catch {
    return false;
  }
};

/**
 * Establish a service session by loading its `/ping` URL inside a hidden
 * iframe. The browser follows the Midway redirect chain inside the
 * iframe, the service responds with `Set-Cookie`, and once the iframe
 * load event fires (plus a short commit delay) the cookie is in place.
 */
const establishServiceSession = (service: Service): Promise<void> =>
  new Promise<void>((resolve, reject) => {
    const url = getAuthUrl(service);
    const iframe = document.createElement('iframe');
    iframe.style.display = 'none';
    iframe.src = url;

    const cleanup = (): void => {
      if (iframe.parentNode) iframe.parentNode.removeChild(iframe);
    };

    const timeoutId = window.setTimeout(() => {
      cleanup();
      reject(new Error(`${service} session warm-up timed out`));
    }, IFRAME_TIMEOUT_MS);

    iframe.onload = () => {
      window.clearTimeout(timeoutId);
      // Give the browser time to commit the Set-Cookie response before
      // we tear the iframe down. Mobile Safari in particular fires
      // `onload` early; without this delay the cookie is lost.
      window.setTimeout(() => {
        cleanup();
        resolve();
      }, COOKIE_COMMIT_DELAY_MS);
    };
    iframe.onerror = () => {
      window.clearTimeout(timeoutId);
      cleanup();
      reject(new Error(`${service} session warm-up failed`));
    };

    document.body.appendChild(iframe);
  });

/**
 * Module-level cache so multiple consumers don't kick off the warm-up
 * more than once per page load. Keyed by alias so a different signed-in
 * user (e.g. dev re-auth) reruns the chain.
 */
const sessionPromiseCache = new Map<string, Promise<void>>();

/**
 * Warm up best-effort services (Starlight/Astra) in the background. Fire-and-forget:
 * probe first, iframe warm-up if missing, and swallow all errors so a failure never
 * affects app readiness or the required warm-up chain.
 */
const warmUpBestEffortServices = (): void => {
  BEST_EFFORT_SERVICES.forEach((service) => {
    void (async () => {
      try {
        if (!(await checkServiceAccess(service))) {
          await establishServiceSession(service);
        }
      } catch {
        // Best-effort: never block the app; the copilot surfaces its own error on use.
      }
    })();
  });
};

/**
 * Warm up every service the app talks to. Strategy mirrors the legacy
 * desktop `AuthHandler`:
 *   1. Probe each service with a top-level credentialed fetch. Any
 *      service that responds OK already has its cookie; skip.
 *   2. For services that failed the probe, run hidden-iframe warm-up
 *      to walk through the Midway → service Set-Cookie chain.
 *
 * The probe is an optimization for return visits where the cookie is
 * already in place. On a fresh session the probe always fails (CORS
 * preflight without a cookie), so we fall through to the iframe path.
 *
 * We trust `iframe.onload` + COOKIE_COMMIT_DELAY_MS as proof that the
 * cookie was set — matching the legacy desktop AuthHandler. We do NOT
 * re-probe after iframe because:
 *   - The probe is a cross-origin fetch that may fail even when the
 *     cookie is correctly set (CORS preflight timing, SameSite rules).
 *   - The legacy code trusts iframe.onload and proceeds to API calls.
 *   - A failed re-probe would trigger a redirect loop on mobile Safari.
 */
const warmUpAllServices = (alias: string): Promise<void> => {
  const cached = sessionPromiseCache.get(alias);
  if (cached) return cached;

  const run = async (): Promise<void> => {
    // Kick off best-effort services (Starlight/Astra) in the background — fire-and-forget,
    // errors swallowed, so they never gate readiness or fail the warm-up chain.
    warmUpBestEffortServices();

    // Probe each required service, then iframe warm-up the ones missing a cookie.
    // Any failure is fatal (propagates and surfaces as an auth error); app readiness
    // gates on all required services.
    const probeResults = await Promise.all(
      REQUIRED_SERVICES.map(async (service) => ({
        service,
        ok: await checkServiceAccess(service),
      })),
    );
    const failedServices = probeResults.filter((r) => !r.ok).map((r) => r.service);
    if (failedServices.length > 0) {
      await Promise.all(failedServices.map(establishServiceSession));
    }
  };

  const promise = run();

  // If the chain fails, drop the cache so a remount can retry instead of
  // being stuck on the failed promise forever.
  promise.catch(() => {
    sessionPromiseCache.delete(alias);
  });

  sessionPromiseCache.set(alias, promise);
  return promise;
};

/**
 * Establishes Orbit + Helios + Starlight session cookies after Midway has
 * handed us an alias.
 *
 * Why this exists: each backing service issues its own SameSite session
 * cookie the first time it sees the Midway-authenticated user. On a
 * fresh browser session those cookies don't exist, and a credentialed
 * cross-origin API call without a cookie is rejected as a CORS failure
 * (with a `(null)` status — opaque to the caller). The warm-up walks
 * each service's `/ping` URL so the service runs its SSO redirect dance
 * and Set-Cookies the SameSite cookie before any business request fires.
 *
 * Mirrors `src/components/auth/AuthHandler.tsx` from the legacy desktop
 * app: probe-first (fast path for return visits), then iframe warm-up
 * with a 1s cookie-commit delay for fresh sessions. We trust the iframe
 * `onload` event as proof of success — matching legacy behavior.
 *
 * @param alias - Authenticated Midway alias. Pass `''` while Midway is
 *                still resolving; the hook stays in the loading state
 *                until a non-empty alias is supplied.
 */
export function useServiceSessions(alias: string): UseServiceSessionsResult {
  const [state, setState] = useState<{
    isLoading: boolean;
    isError: boolean;
    error: Error | null;
  }>({ isLoading: true, isError: false, error: null });

  useEffect(() => {
    if (!alias) {
      // eslint-disable-next-line @eslint-react/hooks-extra/no-direct-set-state-in-use-effect -- resetting to loading when alias clears
      setState({ isLoading: true, isError: false, error: null });
      return;
    }

    let cancelled = false;
    // eslint-disable-next-line @eslint-react/hooks-extra/no-direct-set-state-in-use-effect -- start the warm-up in a loading state
    setState({ isLoading: true, isError: false, error: null });

    warmUpAllServices(alias)
      .then(() => {
        if (!cancelled) setState({ isLoading: false, isError: false, error: null });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const error = err instanceof Error ? err : new Error(String(err));
        setState({ isLoading: false, isError: true, error });
      });

    return () => {
      cancelled = true;
    };
  }, [alias]);

  return {
    isLoading: state.isLoading,
    isReady: !state.isLoading && !state.isError,
    isError: state.isError,
    error: state.error,
  };
}
